import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import heroImage from "../assets/zeamays.png";
import { getBlogPosts } from "../services/blog";
import PageHero from "../components/PageHero";

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  return new Intl.DateTimeFormat("en-NG", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(dateValue));
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPosts = async () => {
      try {
        const blogPosts = await getBlogPosts();
        if (!cancelled) {
          setPosts(blogPosts);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load blog posts.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="min-h-screen bg-[#f7fbef] text-slate-900">
      <PageHero
        eyebrow="Zea Mays Blog"
        title="Stories, recipes, and kitchen notes from Zea Mays."
        description="Read updates, helpful tips, and food inspiration from our kitchen while we keep building the brand."
        image={heroImage}
        imageAlt="Zea Mays blog"
        primaryAction={{ to: "/products", label: "Shop Products" }}
        secondaryAction={{ to: "/contact", label: "Contact Us" }}
        stats={[
          { label: "Recipes", value: "Fresh ideas" },
          { label: "Updates", value: "Brand news" },
          { label: "Tips", value: "Kitchen advice" },
          { label: "Stories", value: "From us" },
        ]}
        compact
      />

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="rounded-lg border border-lime-200 bg-white p-8 text-center text-slate-600">
              Loading blog posts...
            </div>
          ) : null}

          {!loading && !posts.length && !error ? (
            <div className="rounded-lg border border-lime-200 bg-white p-8 text-center text-slate-600">
              No blog posts have been published yet.
            </div>
          ) : null}

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => (
              <article
                key={post.id}
                className="group overflow-hidden rounded-lg border border-lime-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="aspect-[4/3] overflow-hidden bg-lime-100">
                  <img
                    src={post.featured_image || heroImage}
                    alt={post.title}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                    width="640"
                    height="480"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-5">
                  <p className="text-sm font-semibold text-red-700">{formatDate(post.created_at)}</p>
                  <h2 className="mt-3 text-xl font-bold text-slate-950">{post.title}</h2>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">
                    {post.excerpt || post.content}
                  </p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="mt-5 inline-flex rounded-md bg-red-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
                  >
                    Read More
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Blog;
